import React, { useState } from 'react';
import { Segment, Icon, Form, Message, Button } from 'semantic-ui-react';
import { fetcher } from '../lib/fetch';
import useSWR, { mutate } from 'swr';
import { Configurator } from '../Components/FormStore';
import { parseRangeMode, RangeMode } from '../lib/rangeMode';

const configUrl = '/api/v1/config';

async function PostForceRangeMode(lowerMode, upperMode) {
    // use RangeMode to get the uri encoding
    const rangeMode = new RangeMode(lowerMode, upperMode);
    
    // get existing config to preserve other settings
    try {
        let data = await fetcher(configUrl);

        // update range mode in existing config, leave other settings unchanged
        data["range_mode"] = rangeMode.encode();

        const options = {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(data),
        };
        console.log('sumbit', data);
        return fetch(configUrl, options);
    } catch (error) {
        throw new Error("Unable to fetch existing config: " + error.toString());
    }
}

function remap(value, desc) {
  return { value: value, description: desc };
}

const refreshRangeMode = () => {
  mutate(configUrl);
};


const shallowOptions = [
  { key: '1', text: '0.05 m', value: 0 },
  { key: '2', text: '0.3 m', value: 1 },
  { key: '3', text: '1.5 m', value: 2 },
  { key: '4', text: '7.7 m', value: 3 },
  { key: '5', text: '15.4 m', value: 4 },
]

const deepOptions = [
  { key: '1', text: '0.6 m', value: 0 },
  { key: '2', text: '3.0 m', value: 1 },
  { key: '3', text: '14.4 m', value: 2 },
  { key: '4', text: '36.0 m', value: 3 },
  { key: '5', text: 'max', value: 4 },
]

const configuratorOptionsA50 = [
  remap('auto', 'Bottom tracking (auto range)'),
  remap('wt', 'Water tracking'),
  remap('manual', 'Bottom tracking (manual range)'),
]

const configuratorOptionsA125 = [
  remap('auto', 'Bottom tracking (auto range)'),
  remap('wt', 'Water tracking'),
  remap('manual', 'Bottom tracking (manual range)'),
]

function uriToConfiguratorMode(uri) {
  switch (uri) {
    case "auto":
      return "auto"
    case "wt":
      return "wt"
    default:
      return "manual"
    }
}

function configuratorModeToValues(configMode, shallowMode, deepMode) {
  switch (configMode) {
    case 'auto':
      return [-1, -1]
    case 'wt':
      return [-2, -2]
    default:
    // manual mode or fixed
    return [shallowMode, deepMode]
  }
}

const rangeHelpTextA50 = `Range configuration specifies where the DVL searches for valid velocities.
'${configuratorOptionsA50[0].description}' searches for bottom lock between the minimum and maximum altitude that the DVL supports. Water tracking is not used. This is the default option.
'${configuratorOptionsA50[1].description}' calculates the velocities from the water column just below the DVL. Bottom track is not used.
'${configuratorOptionsA50[2].description}' allows you to use a subset of the full range available for bottom track.`

const rangeHelpTextA125 = `Range configuration specifies where the DVL searches for valid velocities.
'${configuratorOptionsA125[0].description}' searches for bottom lock between the minimum and maximum altitude that the DVL supports. Water tracking is not used. This is the default option.
'${configuratorOptionsA125[1].description}' calculates the velocities from the water column just below the DVL. Bottom track is not used.
'${configuratorOptionsA125[2].description}' allows you to use a subset of the full range available for bottom track.`


function RangeConfigurationInner({ URI, deepest, shallowest}) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitSuccess, setIsSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [rangeMode, setRangeMode] = useState(uriToConfiguratorMode(URI));
  const [shallowMode, setShallowMode] = useState(shallowest > 0 ? shallowest : 0);
  const [deepMode, setDeepMode] = useState(deepest > 0 ? deepest : deepOptions.length-1);

  console.log("Range config", URI, rangeMode, shallowMode, deepMode)

  const onSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setIsSubmitSuccess(false);
    setSubmitError('');
    const [lowerMode, upperMode] = configuratorModeToValues(rangeMode, shallowMode, deepMode)
    const result = await PostForceRangeMode(lowerMode, upperMode)
    const body = await result.text();
    if (!result.ok) {
      console.log('submit err', body)
      setSubmitError(body.error);
      setIsSubmitSuccess(false);
    } else {
      console.log('submit success', body);
      setIsSubmitSuccess(true);
      setSubmitError('');
      setTimeout(() => {
        setIsSubmitSuccess(false);
      }, 5000);
    }
    setIsSubmitting(false);
    refreshRangeMode();
  };
  const onChangeCheckBox = (name, value) => {
    console.log("change mode", name, value)
    setRangeMode(value);
    setSubmitError('');
  };

  const handleChange = (e, { name, value }) => {
    if (name === "shallow") {
      setShallowMode(value)
      if (value > deepMode) {
        // Shallow can't be higher than deep
        setDeepMode(value)
      }
    } else {
      setDeepMode(value)
      if (value < shallowMode) {
        // Deep can't be lower than shallow
        setShallowMode(value)
      }
    }
  }

  const { data: aboutData, error: aboutError } = useSWR('/api/v1/about', fetcher);

  if (aboutError) {
    console.log('/api/v1/about load error', aboutError);
  }

  const configuratorOptions =
    aboutData && aboutData.product_name === "DVL A125" ? configuratorOptionsA125 : configuratorOptionsA50;

  const rangeHelpText =
    aboutData && aboutData.product_name === "DVL A125"  ? rangeHelpTextA125 : rangeHelpTextA50;

  return (
    <>
      <Segment attached>
        <Form onSubmit={onSubmit}>
          <Configurator
            heading="Tracking mode"
            name="range"
            help={rangeHelpText}
            value={rangeMode}
            error={''}
            options={configuratorOptions}
            onChange={onChangeCheckBox}
          />

        {rangeMode === "manual" && (
          <>
            <Message
              header="Caution"
              content={`Incorrect settings can result in no velocity even if bottom lock is within range. If you are unsure use: ${configuratorOptions[0].description}`}
            />
            <Form.Group className='top-padding'>
            <Form.Select
              inline
              value={shallowMode}
              name="shallow"
              label="Bottom lock from"
              options={shallowOptions}
              onChange={handleChange}
            />
            <Form.Select
              inline
              value={deepMode}
              name="deep"
              label="Bottom lock to"
              options={deepOptions}
              onChange={handleChange}
            />
            </Form.Group>
          </>
        )}
          <div className='top-padding'>
            <Button
              icon
              positive={submitError === ''}
              negative={submitError !== ''}
              disabled={isSubmitting}
              onClick={onSubmit}
            >Apply
            {isSubmitting && <Icon name="circle notched" loading />}
            {isSubmitSuccess && <Icon name="checkmark" />}
            </Button>
          </div>
          <div className="top-padding">
            {submitError && <Message color="yellow" header="Failed to store" content={submitError} />}
          </div>
        </Form>
      </Segment>
    </>
  );
}

// Advanced range configuration allows setting shallowest and deepest range directly
// in addition to auto and water tracking modes.
// Used in the debug interface.
const AdcancedRangeConfig = () => {
  const { data, error } = useSWR(configUrl, fetcher);
  if (error) {
    console.log('config load error', error);
    return <Message icon="exclamation" header="Cannot load range information" content={'Check network connection'} />;
  }
  if (!data) {
    return <Icon name="spinner" loading />;
  }
  const uri = data.range_mode;
  const rangeMode = parseRangeMode(uri);

  return <RangeConfigurationInner URI={uri} shallowest={rangeMode.shallowest} deepest={rangeMode.deepest} />;
};

export default AdcancedRangeConfig;
